/**
 * Copyright 2025 SweetCode. All rights reserved.
 */

(function (pmw, $, undefined) {

	/**
	 * SSP (Server Side Proxy) state for the current session.
	 *
	 * - null: not yet tested
	 * - true: SSP verified and working
	 * - false: SSP failed, using fallback
	 *
	 * Persisted via pmw.storeData/retrieveData (sessionStorage) so that
	 * once SSP is verified on one page load, subsequent navigations in
	 * the same tab skip re-verification.
	 *
	 * Initialized in _initSspState() after pmw.storeData is available.
	 *
	 * @type {boolean|null}
	 */
	let _sspVerified = null

	/**
	 * Whether we are currently in the process of verifying SSP.
	 * Prevents duplicate verification requests.
	 *
	 * @type {boolean}
	 */
	let _sspVerifying = false

	/**
	 * Queue of payloads waiting for SSP verification to complete.
	 *
	 * @type {Array}
	 */
	let _sspPendingQueue = []

	/**
	 * Read SSP verification state from session storage.
	 * Must be called after pmw.retrieveData is available (i.e. not at module init).
	 *
	 * Also checks for quota exhaustion from both the server-rendered
	 * data layer and any previously stored session flag.
	 */
	const _initSspState = () => {
		if (_sspVerified !== null) return // already set (either from storage or from a verify call)

		// Check server-rendered quota flag (set during PHP sync)
		if (pmwDataLayer?.general?.ssp?.quota_exceeded) {
			pmw.console.log("SSP quota exceeded (data layer flag)")
			_sspVerified = false
			return
		}

		// Check session-level quota flag (set when a 403 was received)
		if (pmw.retrieveData("ssp_quota_exceeded") === true) {
			pmw.console.log("SSP quota exceeded (session flag)")
			_sspVerified = false
			return
		}

		const stored = pmw.retrieveData("ssp_verified")
		if (stored === true) _sspVerified = true
		else if (stored === false) _sspVerified = false
	}

	const isSspEnabled = () => {
		return !!pmwDataLayer?.general?.ssp?.active && !!pmwDataLayer?.general?.ssp?.events_url
	}

	const isSspFallbackEnabled = () => {
		return pmwDataLayer?.general?.ssp?.fallback_to_wc !== false
	}

	/**
	 * Known proxy / translation service hostname suffixes whose requests the
	 * SSP will always reject because their Origin header won't match the
	 * configured shop domain.  Silently dropping them client-side avoids
	 * wasted network round-trips and noisy Event Viewer entries.
	 *
	 * Real mis-configurations (e.g. .co vs .com) are intentionally NOT
	 * blocked here so they still surface in the SSP Event Viewer as
	 * actionable diagnostics.
	 */
	const _sspBlockedOriginSuffixes = [
		".translate.goog",
		".webcache.googleusercontent.com",
	]

	/**
	 * Returns true when the current hostname ends with a known proxy
	 * suffix that the SSP would reject.
	 *
	 * @return {boolean}
	 */
	const isSspOriginBlocked = () => {
		const host = window.location.hostname.toLowerCase()
		return _sspBlockedOriginSuffixes.some(suffix => host.endsWith(suffix))
	}

	/**
	 * Build SSP request headers including security tokens.
	 *
	 * @return {object} Headers object for fetch()
	 */
	const getSspHeaders = () => {
		const headers = {"Content-Type": "application/json"}

		const token = pmwDataLayer?.general?.ssp?.token
		if (token) {
			headers["X-SSP-Token"] = token
		}

		const sessionId = pmwDataLayer?.general?.ssp?.session_id
		if (sessionId) {
			headers["X-SSP-Session"] = sessionId
		}

		return headers
	}

	/**
	 * Send payload to the SSP proxy.
	 *
	 * Uses fetch() with keepalive for all requests (no sendBeacon)
	 * to support custom headers for traffic verification.
	 *
	 * Detects quota exhaustion (403 + request_limit_exceeded) and
	 * stops further SSP requests for the current session.
	 *
	 * @param {object} payload
	 * @return {Promise<boolean>} True if sent successfully.
	 */
	const sendPayloadToSsp = async (payload) => {
		const sspUrl = pmwDataLayer.general.ssp.events_url

		try {
			const response = await fetch(sspUrl, {
				method     : "POST",
				credentials: "include",
				headers    : getSspHeaders(),
				body       : JSON.stringify(payload),
				keepalive  : true,
				redirect   : "error",
			})

			if (response.ok) {
				return true
			}

			// Check for quota exhaustion (403 + request_limit_exceeded)
			if (response.status === 403) {
				try {
					const body = await response.json()
					if (body?.error?.code === "request_limit_exceeded") {
						pmw.console.log("SSP quota exceeded — stopping SSP for this session")
						_sspVerified = false
						pmw.storeData("ssp_quota_exceeded", true)
					}
				} catch (_) {
					// JSON parse failed — treat as generic failure
				}
			}

			pmw.console.log(`SSP fetch: failed (${response.status})`)
			return false
		} catch (e) {
			pmw.console.error("SSP send error:", e)
			return false
		}
	}

	/**
	 * Verify that the SSP proxy is reachable by sending the first payload.
	 *
	 * Also detects quota exhaustion from the response.
	 *
	 * @param {object} payload
	 * @return {Promise<boolean>}
	 */
	const verifySsp = async (payload) => {
		const sspUrl = pmwDataLayer.general.ssp.events_url

		try {
			const response = await fetch(sspUrl, {
				method     : "POST",
				credentials: "include",
				headers    : getSspHeaders(),
				body       : JSON.stringify(payload),
				keepalive  : true,
				redirect   : "error",
			})

			if (response.ok) return true

			// Check for quota exhaustion (403 + request_limit_exceeded)
			if (response.status === 403) {
				try {
					const body = await response.json()
					if (body?.error?.code === "request_limit_exceeded") {
						pmw.console.log("SSP quota exceeded during verification")
						pmw.storeData("ssp_quota_exceeded", true)
					}
				} catch (_) {
					// JSON parse failed — treat as generic failure
				}
			}

			return false
		} catch (e) {
			pmw.console.error("SSP verification failed:", e)
			return false
		}
	}

	/**
	 * Process any payloads that were queued during SSP verification.
	 */
	const drainSspQueue = () => {
		const queue = _sspPendingQueue.splice(0)

		for (const queuedPayload of queue) {
			if (_sspVerified) {
				sendPayloadToSsp(queuedPayload)
			} else if (isSspFallbackEnabled()) {
				sendPayloadViaWc(queuedPayload)
			} else {
				pmw.console.log("SSP unreachable, dropping event (fallback disabled)")
			}
		}
	}

	/**
	 * Send a payload to the WooCommerce S2S endpoint (REST or wp-ajax fallback).
	 *
	 * @param {object} payload
	 */
	const sendPayloadViaWc = async (payload) => {
		if (await pmw.isRestEndpointAvailable()) {

			const blob = new Blob([JSON.stringify(payload)], {type: "application/json; charset=UTF-8"})

			// navigator.sendBeacon has a limit of 64kb and slightly better browser support (96% vs 95%).
			// Fall back to fetch with keepalive if the blob is too large.
			// https://caniuse.com/beacon vs https://caniuse.com/?search=fetch-keepalive
			if (blob.size < 60000 && typeof navigator.sendBeacon === "function") {
				const success = navigator.sendBeacon(pmw.root + "pmw/v1/sse/", blob)
				success ? pmw.console.log("navigator.sendBeacon success") : pmw.console.error("navigator.sendBeacon failed")
			} else {
				fetch(pmw.root + "pmw/v1/sse/", {
					method     : "POST",
					credentials: "same-origin",
					headers    : {"Content-Type": "application/json"},
					body       : JSON.stringify(payload),
					keepalive  : true,
					redirect   : "error",
				})
					.then(response => response.json())
					.then(message => {
						message.success ? pmw.console.log(message) : pmw.console.error(message)
					})
					.catch(error => {
						pmw.console.error("sse error: ", error)
					})
			}
		} else {
			jQuery.ajax({
				type    : "post",
				dataType: "json",
				url     : pmw.ajax_url,
				data    : {
					action: "pmw_server_to_server_event",
					data  : payload,
				},
				success : message => {
					pmw.console.log(message)
				},
				error   : message => {
					pmw.console.error(message)
				},
			})
		}
	}

	pmw.sendEventPayloadToServerPremium = async payload => {

		const isServerToServerEnabled = () => {

			if (pmwDataLayer?.general?.server_2_server?.active) return true

			return false
		}

		const isServerToServerDisabled = () => {
			return !isServerToServerEnabled()
		}

		const storeClientIpInServerSession = async (ip) => {

			pmw.console.log(`Storing client IP address in server session (${ip})`)

			jQuery.ajax({
				type    : "post",
				dataType: "json",
				url     : pmw.ajax_url,
				data    : {
					action: "pmw_store_client_ip_in_server_session",
					data  : {ip},
				},
				success : message => {
					pmw.console.log(message)
					pmw.storeData("store_client_ip_in_server_session", true)
				},
				error   : message => {
					pmw.console.error(message)
					pmw.storeData("store_client_ip_in_server_session", false)
				},
			})
		}

		try {
			// If server to server is disabled, do not send payload
			if (isServerToServerDisabled()) return

			// Check if the visitor is allowed (not a bot or on IP exclusion list)
			if (await pmw.isVisitorNotAllowed()) return

			// If Facebook CAPI is enabled, fetch and store the client IP address
			// Use the centralized getClientIp() which handles race conditions and validation
			if (pmwDataLayer?.pixels?.facebook?.capi) {
				// Use the promise-based IP detection to prevent race conditions
				const clientIp = await pmw.getClientIp()

				// If a WooCommerce session is active
				// and if we have not already sent the IP address to the server
				// and we have a valid client IP
				if (
					clientIp
					&& pmw.isInWooCommerceSesssion()
					&& !pmw.retrieveData("store_client_ip_in_server_session")
				) {
					await storeClientIpInServerSession(clientIp)
				}
			}

			pmw.console.log("Sending event to server", payload)

			// --- SSP Proxy Routing ---
			if (isSspEnabled()) {

				// Silently drop events from known proxy origins (e.g. Google Translate)
				// that the SSP would reject. No fallback — these aren't real visitors.
				if (isSspOriginBlocked()) {
					pmw.console.log(
						`SSP: blocked proxy origin — dropping event`
						+ ` (hostname: ${window.location.hostname})`
					)
					return
				}

				// Hydrate verification state from session storage on first call
				_initSspState()

				// Already verified: route directly
				if (_sspVerified === true) {
					const ok = await sendPayloadToSsp(payload)

					// If SSP returned quota exceeded, fall back for this payload
					if (!ok && _sspVerified === false && isSspFallbackEnabled()) {
						sendPayloadViaWc(payload)
					}
					return
				}

				// Already failed: use WC fallback or drop
				if (_sspVerified === false) {
					if (isSspFallbackEnabled()) {
						sendPayloadViaWc(payload)
					} else {
						pmw.console.log("SSP unreachable, dropping event (fallback disabled)")
					}
					return
				}

				// First event in session – verify SSP reachability
				if (!_sspVerifying) {
					_sspVerifying = true

					verifySsp(payload)
						.then(success => {
							_sspVerified  = success
							_sspVerifying = false

							// Persist for the rest of the browser session (survives page navigations)
							pmw.storeData("ssp_verified", success)

							if (success) {
								pmw.console.log("SSP proxy verified")
							} else {
								pmw.console.log("SSP proxy unreachable, falling back to WC")
							}

							// Process any payloads queued while verifying
							drainSspQueue()
						})
				} else {
					// Verification in progress – queue payload
					_sspPendingQueue.push(payload)
				}

				return
			}

			// --- Standard WC Routing ---
			sendPayloadViaWc(payload)

		} catch (e) {
			console.error(e)
		}
	}

	/**
	 * Sends transient session identifiers to the server.
	 *
	 * @param {object} payload
	 * @return {Promise<void>}
	 */
	pmw.sendTransientSessionIdentifiers = async payload => {

		try {
			jQuery.ajax({
				type    : "post",
				dataType: "json",
				url     : pmw.ajax_url,
				data    : {
					action: "pmw_process_transient_session_identifiers",
					data  : payload,
				},
				success : message => {
					pmw.console.log(message)
				},
				error   : message => {
					pmw.console.error(message)
				},
			})
		} catch (e) {
			console.error(e)
		}
	}

	pmw.acrRemoveCookie = () => {

		// Delete the pmw_checkout_order_created cookie
		// This cookie is used to verify that the purchase confirmation page has been reached

		pmw.deleteCookie("_pmw_automatic_conversion_recovery")
	}

	pmw.getAcrOrderDetails = async () => {

		// Get the order details from the _pmw_automatic_conversion_recovery cookie
		const acrOrderDetails = JSON.parse(pmw.getCookie("_pmw_automatic_conversion_recovery"))

		let response

		// Get order details from the server using the REST API if the REST API is available
		if (await pmw.isRestEndpointAvailable()) {
			response = await fetch(pmw.root + "pmw/v1/acr/order/", {
				method     : "POST",
				credentials: "same-origin",
				cache      : "no-cache",
				headers    : {
					"Content-Type": "application/json",
				},
				body       : JSON.stringify({
					order_id : acrOrderDetails.order_id,
					order_key: acrOrderDetails.order_key,
				}),
			})

		} else {
			// Get order details from the server using the wp-ajax API if the REST API is not available using jQuery
			response = await jQuery.ajax({
				type    : "POST",
				dataType: "json",
				url     : pmw.ajax_url,
				data    : {
					action: "get_acr_order_data_ajax",
					data  : {
						order_id : acrOrderDetails.order_id,
						order_key: acrOrderDetails.order_key,
					},
				},
			})

		}

		const responseJson = await response.json()

		if (responseJson.success) {
			pmw.console.log("ACR order details retrieved successfully")
		} else {
			pmw.console.log("ACR order details could not be retrieved", responseJson)
		}

		pmwDataLayer.products = {...pmwDataLayer.products, ...responseJson.data.products}
		pmwDataLayer.order    = responseJson.data.order
	}

}(window.pmw = window.pmw || {}, jQuery))
